/**
 * Helpers de presentación del dominio de Actividad de usuarios.
 *
 * Puro formato/traducción para la UI; sin lógica de negocio ni acceso a la API
 * (`docs/frontend_architecture.md`, Pr. 6). Cubre la traducción de errores, las
 * etiquetas legibles de módulos/roles (réplica del SSR) y derivaciones para los
 * KPIs (módulo y usuario más activos) y para ordenar mapas clave -> conteo.
 */
import { ApiError } from "@/lib/api";

import {
  ACTIVITY_GENERIC_ERROR,
  MODULE_LABELS,
  ROLE_LABELS,
} from "./constants";
import type { ActivitySummary, ActivityUser } from "./types";

/**
 * Traduce un error de la API/red a un mensaje legible. La actividad es de solo
 * lectura: no hay `error_code` de negocio que mapear, así que se usa el `message`
 * del `ApiError` (ya en español del backend) o del `Error`, con un texto de
 * respaldo. (Un actor multi-sede sin `X-Sede-Id` recibe el mensaje del backend
 * pidiendo la sede, que se muestra tal cual.)
 */
export function activityErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return ACTIVITY_GENERIC_ERROR;
}

/** Etiqueta legible de un módulo (diccionario del SSR); cae al id si no está. */
export function moduleLabel(id: string): string {
  return MODULE_LABELS[id] ?? id;
}

/** Etiqueta legible de un rol (diccionario del SSR); cae al id (o "—") si no está. */
export function roleLabel(role: string): string {
  if (!role) {
    return "—";
  }
  return ROLE_LABELS[role] ?? role;
}

/** Una entrada clave -> conteo, ya ordenada de mayor a menor (para listas/chips). */
export interface CountEntry {
  key: string;
  count: number;
}

/**
 * Convierte un mapa clave -> conteo en una lista ordenada de mayor a menor conteo.
 * Útil para los desgloses por módulo/rol y para los chips de módulos por usuario.
 */
export function sortedCounts(map: Record<string, number>): CountEntry[] {
  return Object.entries(map)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

/** El módulo más usado del resumen, o `null` si no hay actividad. */
export function topModule(summary: ActivitySummary): CountEntry | null {
  const entries = sortedCounts(summary.by_modulo);
  return entries.length > 0 ? entries[0] : null;
}

/**
 * El usuario más activo del resumen, o `null` si no hay actividad. El backend ya
 * devuelve `users` ordenado por total descendente, pero no se asume: se elige el de
 * mayor total explícitamente.
 */
export function topUser(summary: ActivitySummary): ActivityUser | null {
  if (summary.users.length === 0) {
    return null;
  }
  return summary.users.reduce((best, current) =>
    current.total > best.total ? current : best,
  );
}
